// src/components/sla/ai/slaAiFormat.js
// Shared labels, colours and formatters for the SLA AI components (no React).
import {
    MdWarningAmber, MdTrendingUp, MdSwapHoriz, MdHowToVote, MdUndo, MdCheckCircle, MdCancel, MdHourglassTop, MdErrorOutline,
} from 'react-icons/md';

export const GRADE_SCHEME = Object.freeze({ A: 'green', B: 'teal', C: 'yellow', D: 'orange', F: 'red' });

export const ACTION_META = Object.freeze({
    warning: { label: 'At-risk warning', short: 'Warning', scheme: 'orange', icon: MdWarningAmber },
    escalate: { label: 'Escalated to supervisors', short: 'Escalated', scheme: 'red', icon: MdTrendingUp },
    reassign: { label: 'Reassigned by AI', short: 'Reassigned', scheme: 'purple', icon: MdSwapHoriz },
    recommend: { label: 'Reassignment recommended', short: 'Recommended', scheme: 'blue', icon: MdHowToVote },
});

export const STATUS_META = Object.freeze({
    executed: { label: 'Done', scheme: 'gray', icon: MdCheckCircle },
    pending_approval: { label: 'Needs approval', scheme: 'blue', icon: MdHourglassTop },
    approved: { label: 'Approved', scheme: 'green', icon: MdCheckCircle },
    rejected: { label: 'Rejected', scheme: 'red', icon: MdCancel },
    reverted: { label: 'Reverted', scheme: 'orange', icon: MdUndo },
    failed: { label: 'Failed', scheme: 'red', icon: MdErrorOutline },
});

export const OUTCOME_META = Object.freeze({
    met: { label: 'SLA met', scheme: 'green' },
    in_progress: { label: 'In progress', scheme: 'blue' },
    breached_minor: { label: 'Slightly late', scheme: 'orange' },
    breached_major: { label: 'Badly late', scheme: 'red' },
    no_sla: { label: 'No SLA', scheme: 'gray' },
});

export const GRADE_FACTOR_LABELS = Object.freeze({
    timeliness: 'Finished on time',
    response: 'First response',
    quality: 'Work quality',
    compliance: 'Evidence & rework',
});

export const RANK_FACTOR_LABELS = Object.freeze({
    reliability: 'SLA reliability',
    capacity: 'Free capacity',
    proximity: 'Distance',
    experience: 'Similar jobs done',
    team: 'Team match',
});

export const scoreScheme = (v) => (v == null ? 'gray' : v >= 90 ? 'green' : v >= 80 ? 'teal' : v >= 70 ? 'yellow' : v >= 60 ? 'orange' : 'red');

export const fmtMinutes = (m) => {
    if (m == null || !Number.isFinite(Number(m))) return '—';
    const v = Math.max(0, Math.round(Number(m)));
    if (v < 60) return `${v} min`;
    const h = Math.floor(v / 60);
    if (h < 24) return v % 60 ? `${h}h ${v % 60}m` : `${h}h`;
    const d = Math.floor(h / 24);
    return h % 24 ? `${d}d ${h % 24}h` : `${d}d`;
};

export const fmtRelative = (iso, now = Date.now()) => {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return '—';
    const diff = Math.round((now - t) / 60000);
    if (Math.abs(diff) < 1) return 'just now';
    if (diff < 0) return `in ${fmtMinutes(-diff)}`;
    if (diff < 60 * 24) return `${fmtMinutes(diff)} ago`;
    return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

export const fmtDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isFinite(d.getTime()) ? d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
};

export const pct = (v) => (v == null ? '—' : `${Math.round(Number(v))}%`);
